import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Input, 
  Select, 
  Space, 
  Tag, 
  Avatar, 
  Modal, 
  Form, 
  Row, 
  Col,
  message,
  Popconfirm
} from 'antd';
import { 
  UserOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  ExportOutlined
} from '@ant-design/icons';
import { getUserList, deleteUser } from '../services/adminApi';
import AuthGuard from '../components/AuthGuard';
import type { User, UserRole, UserStatus } from '../types';

const { Option } = Select;
const { Search } = Input;

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | undefined>();
  const [statusFilter, setStatusFilter] = useState<UserStatus | undefined>();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '头像',
      dataIndex: 'avatarUrl',
      key: 'avatar',
      width: 80,
      render: (avatarUrl: string, record: User) => (
        <Avatar src={avatarUrl} icon={<UserOutlined />} />
      ),
    },
    {
      title: '用户名',
      dataIndex: 'nickname',
      key: 'nickname',
      render: (text: string) => text || '未设置',
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone ? phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : '-',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: UserRole) => {
        const colors: Record<UserRole, string> = {
          admin: 'red',
          level3: 'purple',
          level2: 'gold',
          level1: 'green',
          retail: 'blue',
        };
        return <Tag color={colors[role]}>{roleMap[role]}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: UserStatus) => {
        const colors: Record<UserStatus, string> = {
          active: 'success',
          inactive: 'default',
          suspended: 'error',
        };
        return <Tag color={colors[status]}>{statusMap[status]}</Tag>;
      },
    },
    {
      title: '推荐码',
      dataIndex: 'referralCode',
      key: 'referralCode',
      render: (code: string) => <code>{code}</code>,
    },
    {
      title: '企业名称',
      dataIndex: ['userProfile', 'companyName'],
      key: 'companyName',
      render: (companyName: string) => companyName || '-',
    },
    {
      title: '真实姓名',
      dataIndex: ['userProfile', 'realName'],
      key: 'realName',
      render: (realName: string) => realName || '-',
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: User) => (
        <Space size="small">
          <Button
            type="link"
            icon={<UserOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详细
          </Button>
          <Popconfirm
            title="删除用户"
            description="确定要删除该用户吗？此操作将同时删除用户的所有关联数据，包括资料、银行卡、推广记录、业绩记录、分润记录、提现记录、登录日志等，且无法恢复！"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination: any) => {
    fetchUsers(pagination.current, pagination.pageSize);
  };

  // 获取用户列表
  const fetchUsers = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await getUserList({
        page,
        pageSize,
        keyword: searchText || undefined,
        role: roleFilter,
        status: statusFilter,
      });

      if (response.data.success) {
        setUsers(response.data.data.list);
        setPagination({
          current: response.data.data.page,
          pageSize: response.data.data.pageSize,
          total: response.data.data.total,
        });
      }
    } catch (error: any) {
      message.error(error.message || '获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchText, roleFilter, statusFilter]);

  const roleMap: Record<UserRole, string> = {
    admin: '管理员',
    level3: '三级推荐官',
    level2: '二级推荐官',
    level1: '一级推荐官',
    retail: '用电用户'
  };

  const statusMap: Record<UserStatus, string> = {
    active: '活跃',
    inactive: '非活跃',
    suspended: '已暂停'
  };

  const handleAdd = () => {
    message.info('新增用户功能暂未开放，请使用小程序注册');
  };

  const handleEdit = (user: User) => {
    message.info('编辑用户功能开发中...');
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await deleteUser(id);

      if (response.data.success) {
        message.success('删除用户成功');
        // 刷新用户列表
        fetchUsers(pagination.current, pagination.pageSize);
      } else {
        message.error(response.data.message || '删除用户失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || error.message || '删除用户失败');
    }
  };

  const handleGenerateData = () => {
    message.info('生成数据功能已移除，请使用真实API数据');
  };

  const handleExport = () => {
    message.info('导出功能开发中...');
  };

  const handleViewDetail = (user: User) => {
    setSelectedUser(user);
    setDetailModalVisible(true);
  };

  return (
    <AuthGuard>
      <div>
        <Card>
          <div style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={6}>
                <Search
                  placeholder="搜索用户名、手机号或邀请码"
                  onSearch={setSearchText}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={6} md={4}>
                <Select
                  placeholder="筛选角色"
                  allowClear
                  value={roleFilter}
                  onChange={setRoleFilter}
                  style={{ width: '100%' }}
                >
                  <Option value="retail">用电用户</Option>
                  <Option value="level1">一级推荐官</Option>
                  <Option value="level2">二级推荐官</Option>
                  <Option value="level3">三级推荐官</Option>
                </Select>
              </Col>
              <Col xs={24} sm={6} md={4}>
                <Select
                  placeholder="筛选状态"
                  allowClear
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: '100%' }}
                >
                  <Option value="active">活跃</Option>
                  <Option value="inactive">非活跃</Option>
                  <Option value="suspended">已暂停</Option>
                </Select>
              </Col>
              <Col xs={24} sm={24} md={10}>
                <Space>
                  <Button icon={<SearchOutlined />} onClick={() => fetchUsers()}>
                    搜索
                  </Button>
                  <Button icon={<ExportOutlined />} onClick={handleExport}>
                    导出数据
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>

          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
          />
        </Card>

        {/* 用户详细信息Modal */}
        <Modal
          title="用户详细信息"
          open={detailModalVisible}
          onCancel={() => setDetailModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setDetailModalVisible(false)}>
              关闭
            </Button>,
          ]}
          width={800}
        >
          {selectedUser && (
            <div>
              <h3>基本信息</h3>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <strong>ID:</strong> {selectedUser.id}
                </Col>
                <Col span={12}>
                  <strong>用户名:</strong> {selectedUser.nickname || '未设置'}
                </Col>
                <Col span={12}>
                  <strong>手机号:</strong> {selectedUser.phone || '-'}
                </Col>
                <Col span={12}>
                  <strong>邮箱:</strong> {selectedUser.email || '-'}
                </Col>
                <Col span={12}>
                  <strong>角色:</strong> <Tag color="blue">{roleMap[selectedUser.role]}</Tag>
                </Col>
                <Col span={12}>
                  <strong>状态:</strong> <Tag color="green">{statusMap[selectedUser.status]}</Tag>
                </Col>
                <Col span={12}>
                  <strong>推荐码:</strong> <code>{selectedUser.referralCode}</code>
                </Col>
                <Col span={12}>
                  <strong>父级ID:</strong> {selectedUser.parentId || '-'}
                </Col>
                <Col span={12}>
                  <strong>OpenID:</strong> {selectedUser.openid || '-'}
                </Col>
                <Col span={12}>
                  <strong>UnionID:</strong> {selectedUser.unionid || '-'}
                </Col>
                <Col span={12}>
                  <strong>是否超级管理员:</strong> {selectedUser.isSuperAdmin ? '是' : '否'}
                </Col>
                <Col span={24}>
                  <strong>头像URL:</strong> {selectedUser.avatarUrl || '-'}
                </Col>
                <Col span={12}>
                  <strong>注册时间:</strong> {new Date(selectedUser.createdAt).toLocaleString()}
                </Col>
                <Col span={12}>
                  <strong>更新时间:</strong> {new Date(selectedUser.updatedAt).toLocaleString()}
                </Col>
              </Row>

              {selectedUser.userProfile && (
                <>
                  <h3 style={{ marginTop: '24px' }}>用户资料</h3>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <strong>企业名称:</strong> {selectedUser.userProfile.companyName || '-'}
                    </Col>
                    <Col span={12}>
                      <strong>营业执照号:</strong> {selectedUser.userProfile.businessLicense || '-'}
                    </Col>
                    <Col span={12}>
                      <strong>联系人:</strong> {selectedUser.userProfile.contactPerson || '-'}
                    </Col>
                    <Col span={12}>
                      <strong>联系电话:</strong> {selectedUser.userProfile.contactPhone || '-'}
                    </Col>
                    <Col span={12}>
                      <strong>联系邮箱:</strong> {selectedUser.userProfile.contactEmail || '-'}
                    </Col>
                    <Col span={12}>
                      <strong>地址:</strong> {selectedUser.userProfile.address || '-'}
                    </Col>
                    <Col span={12}>
                      <strong>经营范围:</strong> {selectedUser.userProfile.businessScope || '-'}
                    </Col>
                    <Col span={12}>
                      <strong>注册资本:</strong> {selectedUser.userProfile.registeredCapital || '-'}
                    </Col>
                    <Col span={12}>
                      <strong>成立日期:</strong> {selectedUser.userProfile.establishedDate
                        ? new Date(selectedUser.userProfile.establishedDate).toLocaleDateString()
                        : '-'}
                    </Col>
                    <Col span={12}>
                      <strong>真实姓名:</strong> {selectedUser.userProfile.realName || '-'}
                    </Col>
                    <Col span={12}>
                      <strong>身份证号:</strong> {selectedUser.userProfile.idCard || '-'}
                    </Col>
                    <Col span={12}>
                      <strong>是否实名认证:</strong> {selectedUser.userProfile.isVerified ? '是' : '否'}
                    </Col>
                    <Col span={12}>
                      <strong>认证时间:</strong> {selectedUser.userProfile.verifiedAt
                        ? new Date(selectedUser.userProfile.verifiedAt).toLocaleString()
                        : '-'}
                    </Col>
                    <Col span={12}>
                      <strong>身份证正面:</strong> {selectedUser.userProfile.idCardFront ? '已上传' : '-'}
                    </Col>
                    <Col span={12}>
                      <strong>身份证背面:</strong> {selectedUser.userProfile.idCardBack ? '已上传' : '-'}
                    </Col>
                  </Row>
                </>
              )}

              <h3 style={{ marginTop: '24px' }}>银行卡信息</h3>
              {selectedUser.bankCards && selectedUser.bankCards.length > 0 ? (
                selectedUser.bankCards.map((card, index) => (
                  <div key={card.id} style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e8e8e8', borderRadius: '4px' }}>
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <strong>银行:</strong> {card.bankName}
                      </Col>
                      <Col span={12}>
                        <strong>卡号:</strong> {card.cardNumber.replace(/(\d{4})\d*(\d{4})/, '$1 **** $2')}
                      </Col>
                      <Col span={12}>
                        <strong>持卡人:</strong> {card.cardHolder}
                      </Col>
                      <Col span={12}>
                        <strong>是否默认:</strong> {card.isDefault ? '是' : '否'}
                      </Col>
                      <Col span={12}>
                        <strong>创建时间:</strong> {new Date(card.createdAt).toLocaleString()}
                      </Col>
                    </Row>
                  </div>
                ))
              ) : (
                <p style={{ color: '#999' }}>暂无银行卡信息</p>
              )}
            </div>
          )}
        </Modal>
      </div>
    </AuthGuard>
  );
};

export default UserManagement;